import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import _ from "lodash";

import { StatCard, StatCardGrid } from "../../common";
import { useReportSummaryQuery } from "src/query/experiences/leadsAndReports/reports";
import { selectDateFilter, selectReportType } from "src/redux/selectors/experiences/leadsAndReports";
import { convertDatesToTimezone } from "src/services/experiences/leadsAndReports/dateUtils";
import { REPORT_SUMMARY_LEFT, isInteractive } from "../../constants";
import { formatDurationWithLabel, formatNumber } from "../../utils/format";

interface ReportSummaryProps {
  storyboardId: string;
  storyboardType?: string;
}

export const ReportSummary: React.FC<ReportSummaryProps> = ({ storyboardId, storyboardType }) => {
  const reportType = useSelector(selectReportType);
  const dateFilter = useSelector(selectDateFilter);

  const request = {
    storyboard: storyboardId,
    reportType,
    ...convertDatesToTimezone(dateFilter ?? undefined),
  };

  const { data: summary, isLoading: summaryLoading } = useReportSummaryQuery(
    storyboardId,
    request,
    Boolean(dateFilter),
  );

  const s = summary ?? {};

  const tiles = useMemo(() => {
    const isDuration = (key: string) => key === "totalTimeSpent" || key === "averageTimeSpent";
    const list = REPORT_SUMMARY_LEFT[reportType].map((item) => {
      if (isDuration(item.accessorKey)) {
        const duration = formatDurationWithLabel(_.get(s, item.accessorKey));
        return { path: item.path, title: item.title, value: duration.value, subValue: "" };
      }
      return {
        path: item.path,
        title: item.title,
        value: formatNumber(_.get(s, item.accessorKey) ?? null),
        subValue: "",
      };
    });

    if (isInteractive(storyboardType)) {
      list.push({
        path: "/images/experiences/reports/downloadV2.svg",
        title: "Download Total Number",
        value: formatNumber((s.downloads as number) ?? null),
        subValue: "",
      });
    }
    return list;
  }, [s, reportType, storyboardType]);

  return (
      <StatCardGrid>
        {tiles.map((tile, i) => (
          <StatCard
            key={i}
            icon={tile.path}
            heading={tile.title}
            value={tile.value as React.ReactNode}
            subValue={tile.subValue}
            loading={!dateFilter || summaryLoading}
          />
        ))}
      </StatCardGrid>
  );
};
